import config from '../config/environment';
import { authService } from './auth';

const API_URL = config.API_URL;

const GEOIP_API_URL = process.env.REACT_APP_GEOIP_API_URL;
const GEOIP_API_TOKEN = process.env.REACT_APP_GEOIP_API_TOKEN;

const getGeoLocation = async (ip) => {
  const res = await fetch(`${GEOIP_API_URL}/${ip}/json?token=${GEOIP_API_TOKEN}`);
  return res.json();
};


// ✅ Helper function to convert relative URLs to absolute URLs
const convertToAbsoluteUrl = (url) => {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = config.API_URL.replace(/\/api$/, '');
  return `${baseUrl}${url}`;
};

// ✅ Validate if a string is a valid URL
const isValidUrl = (urlString) => {
  try {
    new URL(urlString);
    return true;
  } catch (err) {
    return false;
  }
};

// ✅ Enrich candidate data with alternative field names
const enrichCandidateData = (candidate) => {
  return {
    ...candidate,
    // Handle different field name cases
    affidavitPdfUrl: candidate.affidavitPdfUrl || candidate.affidavit_pdf_url || candidate.AffidavitPdfUrl || null,
    photoUrl: candidate.photoUrl || candidate.photo_url || candidate.PhotoUrl || null,
    symbolUrl: candidate.symbolUrl || candidate.symbol_url || candidate.SymbolUrl || null,
    partySymbolUrl: candidate.partySymbolUrl || candidate.party_symbol_url || candidate.PartySymbolUrl || null,
  };
};

// ✅ Enhanced Generic API call function
export const apiCall = async (endpoint, options = {}) => {
  const token = authService.getToken();

  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const fetchConfig = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, fetchConfig);

    if (response.status === 401) {
      authService.logout();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (response.status === 204) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorJson.error || errorJson.title || 'Request failed';
      } catch {
        errorMessage = errorText || `Request failed with status ${response.status}`;
      }

      console.error('API Error Response:', errorMessage);
      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// ==================== NEWS API ====================
export const getBiharElectionNews = async () => {
  try {
    console.log('📡 Fetching Bihar election news from:', `${API_URL}/News/bihar-election`);

    const response = await fetch(`${API_URL}/News/bihar-election`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('📥 News response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ News API Error:', errorText);
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Bihar news data received:', data);
    return data;
  } catch (error) {
    console.error('❌ Bihar news fetch error:', error);
    throw error;
  }
};

// ==================== Main API Export ====================
export const api = {
  // ==================== CONSTITUENCIES ====================
  getConstituencies: () => apiCall('/Constituency'),
  getConstituencyById: (id) => apiCall(`/Constituency/${id}`),
  getConstituencyResults: (id) => apiCall(`/Constituency/${id}/results`),
  getDistricts: () => {
    console.log('📍 Fetching all districts');
    return apiCall('/Constituency/districts');
  },
  getConstituenciesByDistrict: (district) => {
    console.log('📍 Fetching constituencies for district:', district);
    return apiCall(`/Constituency/by-district/${encodeURIComponent(district)}`);
  },

  // ==================== CANDIDATES (PUBLIC) ====================
  getAllCandidates: () => apiCall('/Candidate/all-nominations'),
  
  // ✅ UPDATED: Get candidate by ID with enriched data
  getCandidateById: async (id) => {
    try {
      console.log('📥 Fetching candidate:', id);
      const data = await apiCall(`/Candidate/${id}`);
      
      // ✅ Enrich data with affidavit URL
      const enrichedData = enrichCandidateData(data);
      
      console.log('✅ Candidate data enriched:', enrichedData);
      console.log('📄 Affidavit URL:', enrichedData.affidavitPdfUrl);
      console.log('📄 All keys in response:', Object.keys(data));
      
      return enrichedData;
    } catch (error) {
      console.error('❌ Error fetching candidate:', error);
      throw error;
    }
  },

  getNominationsByConstituency: () => apiCall('/Candidate/nominations-by-constituency'),
  
  getCandidatesByConstituency: (constituencyCode) => {
    console.log('📥 Fetching candidates for constituency:', constituencyCode);
    return apiCall(`/Candidate/constituency/${constituencyCode}`);
  },

  // ==================== VOTER SURVEY - CANDIDATES ====================
  getCandidatesByConstituencyName: (constituencyName) => {
    console.log('👥 Fetching candidates for constituency:', constituencyName);
    return apiCall(
      `/VoterSurvey/candidates/by-constituency/${encodeURIComponent(constituencyName)}`
    );
  },

  getCandidatesByDistrict: (district) => {
    console.log('👥 Fetching candidates for district:', district);
    return apiCall(`/VoterSurvey/candidates/by-district/${encodeURIComponent(district)}`);
  },

  getAllConstituencies: () => {
    console.log('📍 Fetching all constituencies');
    return apiCall('/VoterSurvey/constituencies/all');
  },

  // ==================== RESULTS ====================
  getAllResults: () => apiCall('/Admin/all-results'),
  getStatistics: () => apiCall('/Admin/statistics'),

  // ==================== VOTER CERTIFICATE (PUBLIC) ====================
uploadVoterCertificate: async (formData) => {
  const token = authService.getToken();

  try {
    console.log('📸 Starting voter certificate upload...');
    console.log('📦 FormData entries:');

    const entries = Array.from(formData.entries());
    entries.forEach(([key, value]) => {
      if (value instanceof File) {
        console.log(`  ✓ ${key}: File { name: "${value.name}", size: ${value.size} bytes, type: "${value.type}" }`);
      } else {
        console.log(`  ✓ ${key}: "${value}" (${typeof value})`);
      }
    });

    console.log(`📤 POST ${API_URL}/VoterCertificate/upload-selfie`);

    const response = await fetch(`${API_URL}/VoterCertificate/upload-selfie`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    });

    console.log('📥 Response Status:', response.status, response.statusText);

    const responseText = await response.text();
    console.log('📋 Response Body (raw):', responseText);

    if (response.status === 400) {
      console.error('❌ Bad Request (400)');
      try {
        const errorJson = JSON.parse(responseText);
        console.error('❌ Error Details:', errorJson);

        if (errorJson.errors) {
          console.error('❌ Validation Errors:');
          Object.entries(errorJson.errors).forEach(([field, messages]) => {
            console.error(`  - ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`);
          });
        }

        throw new Error(errorJson.message || JSON.stringify(errorJson.errors) || 'Bad request - invalid data format');
      } catch (parseErr) {
        console.error('❌ Could not parse error JSON:', parseErr);
        throw new Error(`Bad request: ${responseText}`);
      }
    }

    if (response.status === 401) {
      console.error('❌ Unauthorized (401)');
      authService.logout();
      window.location.href = '/login';
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      console.error(`❌ HTTP Error ${response.status}`);
      throw new Error(`Upload failed with status ${response.status}: ${responseText}`);
    }

    let result;
    try {
      result = JSON.parse(responseText);
    } catch {
      result = { success: true, message: 'Certificate uploaded' };
    }

    console.log('✅ Certificate uploaded successfully');
    console.log('✅ Response Data:', result);
    return result;
  } catch (error) {
    console.error('❌ Certificate Upload Error');
    console.error('  Message:', error.message);
    console.error('  Full Error:', error);
    throw error;
  }
},

checkVoterCertificate: (phoneNumber) => {
  console.log('📱 Checking certificate for phone:', phoneNumber);
  return apiCall(`/VoterCertificate/check/${phoneNumber}`);
},

getCertificateById: (certificateId) => {
  console.log('📄 Fetching certificate:', certificateId);
  return apiCall(`/VoterCertificate/certificate/${certificateId}`);
},

getCertificateImage: (fileName) => {
  return `${API_URL}/VoterCertificate/image/${fileName}`;
},

// ==================== VOTER CERTIFICATE (ADMIN) ====================

// ✅ Get all voter certificates with pagination
getAllVoterCertificates: (page = 1, pageSize = 20) => {
  console.log(`📋 Admin: Fetching all certificates - Page: ${page}, Size: ${pageSize}`);
  return apiCall(`/VoterCertificate/all?page=${page}&pageSize=${pageSize}`);
},

// ✅ Get voter certificate statistics
getVoterCertificateStats: () => {
  console.log('📊 Admin: Fetching certificate statistics');
  return apiCall('/VoterCertificate/stats');
},

// ✅ Get unique districts with stats
getCertificateDistricts: () => {
  console.log('📍 Admin: Fetching certificate districts');
  return apiCall('/VoterCertificate/districts');
},

// ✅ Get unique constituencies with stats
getCertificateConstituencies: () => {
  console.log('🏛️ Admin: Fetching certificate constituencies');
  return apiCall('/VoterCertificate/constituencies');
},

// ✅ Search voter certificates
searchVoterCertificates: (query) => {
  console.log('🔍 Admin: Searching certificates:', query);
  return apiCall(`/VoterCertificate/search?query=${encodeURIComponent(query)}`);
},

// ✅ Filter certificates by district
getCertificatesByDistrict: (district, page = 1, pageSize = 20) => {
  console.log(`📍 Admin: Filtering by district: ${district}`);
  return apiCall(`/VoterCertificate/by-district/${encodeURIComponent(district)}?page=${page}&pageSize=${pageSize}`);
},

// ✅ Filter certificates by constituency
getCertificatesByConstituency: (constituency, page = 1, pageSize = 20) => {
  console.log(`🏛️ Admin: Filtering by constituency: ${constituency}`);
  return apiCall(`/VoterCertificate/by-constituency/${encodeURIComponent(constituency)}?page=${page}&pageSize=${pageSize}`);
},

// ✅ Get voter certificate by certificate ID (admin detail view)
getVoterCertificateById: (certificateId) => {
  console.log('📄 Admin: Fetching certificate by ID:', certificateId);
  return apiCall(`/VoterCertificate/certificate/${certificateId}`);
},

// ✅ LEGACY SUPPORT (optional - maps to new methods)
getCertificateStats: () => {
  console.log('📊 Fetching certificate statistics (legacy)');
  return apiCall('/VoterCertificate/stats');
},

getAllCertificates: (page = 1, pageSize = 20) => {
  console.log(`📋 Fetching certificates (legacy) - Page: ${page}, Size: ${pageSize}`);
  return apiCall(`/VoterCertificate/all?page=${page}&pageSize=${pageSize}`);
},

searchCertificates: (query) => {
  console.log('🔍 Searching certificates (legacy):', query);
  return apiCall(`/VoterCertificate/search?query=${encodeURIComponent(query)}`);
},

  // ==================== VOTER SURVEY (PUBLIC) ====================
submitVoterSurvey: async (surveyData) => {
  try {
    console.log('📝 Submitting voter survey');
    console.log('📦 Survey Data:', surveyData);

    const response = await fetch(`${API_URL}/VoterSurvey/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(surveyData)
    });

    console.log('📥 Survey response status:', response.status);

    const responseText = await response.text();
    console.log('📋 Response:', responseText);

    if (!response.ok) {
      try {
        const errorJson = JSON.parse(responseText);
        throw new Error(errorJson.message || errorJson.error || 'Survey submission failed');
      } catch (parseErr) {
        throw new Error(responseText || 'Survey submission failed');
      }
    }

    const result = JSON.parse(responseText);
    console.log('✅ Survey submitted successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Survey submission error:', error);
    throw error;
  }
},

checkVoterSurvey: (phoneNumber) => {
  console.log('📱 Checking survey for phone:', phoneNumber);
  return apiCall(`/VoterSurvey/check`, {
    method: 'POST',
    body: JSON.stringify({ phoneNumber })
  });
},

getSurveyById: (surveyId) => {
  console.log('📄 Fetching survey:', surveyId);
  return apiCall(`/VoterSurvey/${surveyId}`);
},

// ==================== VOTER SURVEY (ADMIN) ====================

// ✅ Get all voter surveys with pagination
getAllVoterSurveys: (page = 1, pageSize = 20) => {
  console.log(`📋 Admin: Fetching all surveys - Page: ${page}, Size: ${pageSize}`);
  return apiCall(`/VoterSurvey/all?page=${page}&pageSize=${pageSize}`);
},

// ✅ Get voter survey statistics
getVoterSurveyStats: () => {
  console.log('📊 Admin: Fetching survey statistics');
  return apiCall('/VoterSurvey/stats');
},

// ✅ Get unique districts with survey counts
getSurveyDistricts: () => {
  console.log('📍 Admin: Fetching survey districts');
  return apiCall('/VoterSurvey/districts');
},

// ✅ Search voter surveys
searchVoterSurveys: (query) => {
  console.log('🔍 Admin: Searching surveys:', query);
  return apiCall(`/VoterSurvey/search?query=${encodeURIComponent(query)}`);
},

// ✅ Filter surveys by district
getSurveysByDistrict: (district, page = 1, pageSize = 20) => {
  console.log(`📍 Admin: Filtering surveys by district: ${district}`);
  return apiCall(`/VoterSurvey/by-district/${encodeURIComponent(district)}?page=${page}&pageSize=${pageSize}`);
},

// ✅ Filter surveys by constituency
getSurveysByConstituency: (constituency, page = 1, pageSize = 20) => {
  console.log(`🏛️ Admin: Filtering surveys by constituency: ${constituency}`);
  return apiCall(`/VoterSurvey/by-constituency/${encodeURIComponent(constituency)}?page=${page}&pageSize=${pageSize}`);
},

// ✅ Filter surveys by candidate
getSurveysByCandidate: (candidateName, page = 1, pageSize = 20) => {
  console.log(`👤 Admin: Filtering surveys by candidate: ${candidateName}`);
  return apiCall(`/VoterSurvey/by-candidate/${encodeURIComponent(candidateName)}?page=${page}&pageSize=${pageSize}`);
},

// ✅ Get voter survey by survey ID (admin detail view)
getVoterSurveyById: (surveyId) => {
  console.log('📄 Admin: Fetching survey by ID:', surveyId);
  return apiCall(`/VoterSurvey/${surveyId}`);
},

  // ==================== 🆕 POLL DATA MANAGEMENT ====================

submitPollData: async (pollData) => {
  try {
    console.log('📊 Submitting poll data');
    console.log('📦 Poll Data:', pollData);

    return await apiCall('/PollData/submit', {
      method: 'POST',
      body: JSON.stringify(pollData)
    });
  } catch (error) {
    console.error('❌ Poll data submission error:', error);
    throw error;
  }
},

getPollDataByPhase: async (phaseNumber, page = 1, pageSize = 10) => {
  try {
    console.log(`📊 Fetching poll data for phase: ${phaseNumber} - Page: ${page}, Size: ${pageSize}`);

    // Validate phase number
    if (!phaseNumber || phaseNumber < 1 || phaseNumber > 8) {
      throw new Error('Phase number must be between 1 and 8');
    }

    const response = await apiCall(
      `/PollData/phase/${phaseNumber}?page=${page}&pageSize=${pageSize}`,
      { method: 'GET' }
    );

    console.log(`✅ Poll data fetched for phase ${phaseNumber}:`, response);
    return response;
  } catch (error) {
    console.error(`❌ Error fetching poll data for phase ${phaseNumber}:`, error);
    throw error;
  }
},

getPollDataByDistrict: async (districtName, phaseNumber = 1) => {
  try {
    console.log(`📍 Fetching poll data for district: ${districtName}, phase: ${phaseNumber}`);

    if (!districtName || districtName.trim() === '') {
      throw new Error('District name is required');
    }

    if (phaseNumber < 1 || phaseNumber > 8) {
      throw new Error('Phase number must be between 1 and 8');
    }

    const response = await apiCall(
      `/PollData/by-district/${encodeURIComponent(districtName.trim())}?phaseNumber=${phaseNumber}`,
      { method: 'GET' }
    );

    console.log(`✅ Poll data fetched for district ${districtName}:`, response);
    return response;
  } catch (error) {
    console.error(`❌ Error fetching poll data for district ${districtName}:`, error);
    throw error;
  }
},

getDistrictPollingStats: async (phaseNumber = 1) => {
  try {
    console.log(`📊 Fetching district polling statistics for phase: ${phaseNumber}`);

    if (phaseNumber < 1 || phaseNumber > 8) {
      throw new Error('Phase number must be between 1 and 8');
    }

    const response = await apiCall(
      `/PollData/districts/stats?phaseNumber=${phaseNumber}`,
      { method: 'GET' }
    );

    console.log(`✅ District polling stats fetched:`, response);
    return response;
  } catch (error) {
    console.error('❌ Error fetching district polling stats:', error);
    throw error;
  }
},

getAllPollData: async (page = 1, pageSize = 20) => {
  try {
    console.log(`📋 Fetching all poll data - Page: ${page}, Size: ${pageSize}`);

    if (page < 1) {
      throw new Error('Page number must be greater than 0');
    }

    if (pageSize < 1 || pageSize > 100) {
      throw new Error('Page size must be between 1 and 100');
    }

    const response = await apiCall(
      `/PollData/all?page=${page}&pageSize=${pageSize}`,
      { method: 'GET' }
    );

    console.log(`✅ All poll data fetched:`, response);
    return response;
  } catch (error) {
    console.error('❌ Error fetching all poll data:', error);
    throw error;
  }
},

deletePollData: async (id) => {
  try {
    if (!id) {
      throw new Error('Poll data ID is required');
    }

    console.log(`🗑️ Deleting poll data: ${id}`);

    const response = await apiCall(
      `/PollData/${id}`,
      { method: 'DELETE' }
    );

    console.log(`✅ Poll data deleted successfully:`, response);
    return response;
  } catch (error) {
    console.error(`❌ Error deleting poll data (ID: ${id}):`, error);
    throw error;
  }
},

updatePollData: async (id, pollData) => {
  try {
    if (!id) {
      throw new Error('Poll data ID is required');
    }

    console.log(`📤 Updating poll data: ${id}`);
    console.log('📦 Poll Data:', pollData);

    const response = await apiCall(
      `/PollData/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(pollData)
      }
    );

    console.log(`✅ Poll data updated successfully:`, response);
    return response;
  } catch (error) {
    console.error(`❌ Error updating poll data (ID: ${id}):`, error);
    throw error;
  }
},

// ==================== 🆕 VALIDATION HELPER ====================

validateCandidateData: (data) => {
  const errors = [];

  if (!data.constituencyCode?.trim()) {
    errors.push('Constituency code is required');
  }

  if (!data.candidateName?.trim()) {
    errors.push('Candidate name is required');
  }

  if (!data.partyShortName?.trim()) {
    errors.push('Party short name is required');
  }

  if (!data.partyName?.trim()) {
    errors.push('Party name is required');
  }

  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
    errors.push('Invalid email address format');
  }

  if (data.mobile) {
    const cleanMobile = String(data.mobile).replace(/\D/g, '');
    if (!/^\d{10,15}$/.test(cleanMobile)) {
      errors.push('Mobile number must be 10-15 digits');
    }
  }

  if (data.age && (isNaN(data.age) || data.age < 18 || data.age > 120)) {
    errors.push('Age must be a valid number between 18 and 120');
  }

  if (data.affidavitPdfUrl) {
    const absoluteUrl = convertToAbsoluteUrl(data.affidavitPdfUrl.trim());
    if (!isValidUrl(absoluteUrl)) {
      errors.push(`Invalid affidavit PDF URL: ${absoluteUrl}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
},

// ==================== 🆕 POLL DATA VALIDATION ====================

validatePollData: (data) => {
  const errors = [];

  if (!data.constituencyId || isNaN(data.constituencyId)) {
    errors.push('Valid constituency ID is required');
  }

  if (!data.phaseNumber || data.phaseNumber < 1 || data.phaseNumber > 8) {
    errors.push('Phase number must be between 1 and 8');
  }

  if (!data.pollDate) {
    errors.push('Poll date is required');
  }

  if (
    data.overallPercentage === undefined ||
    isNaN(data.overallPercentage) ||
    data.overallPercentage < 0 ||
    data.overallPercentage > 100
  ) {
    errors.push('Overall percentage must be a number between 0 and 100');
  }

  if (
    data.malePercentage === undefined ||
    isNaN(data.malePercentage) ||
    data.malePercentage < 0 ||
    data.malePercentage > 100
  ) {
    errors.push('Male percentage must be a number between 0 and 100');
  }

  if (
    data.femalePercentage === undefined ||
    isNaN(data.femalePercentage) ||
    data.femalePercentage < 0 ||
    data.femalePercentage > 100
  ) {
    errors.push('Female percentage must be a number between 0 and 100');
  }

  if (data.totalVoters && (isNaN(data.totalVoters) || data.totalVoters < 0)) {
    errors.push('Total voters must be a positive number');
  }

  if (data.maleVoters && (isNaN(data.maleVoters) || data.maleVoters < 0)) {
    errors.push('Male voters must be a positive number');
  }

  if (data.femaleVoters && (isNaN(data.femaleVoters) || data.femaleVoters < 0)) {
    errors.push('Female voters must be a positive number');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
},

// ==================== 🆕 GENERIC CRUD OPERATIONS ====================

get: async (endpoint) => {
  console.log(`🔵 GET ${endpoint}`);
  try {
    return await apiCall(endpoint, { method: 'GET' });
  } catch (error) {
    console.error(`❌ GET ${endpoint} failed:`, error);
    throw error;
  }
},

post: async (endpoint, data) => {
  console.log(`🔵 POST ${endpoint}`, data);
  try {
    return await apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  } catch (error) {
    console.error(`❌ POST ${endpoint} failed:`, error);
    throw error;
  }
},

put: async (endpoint, data) => {
  console.log(`🔵 PUT ${endpoint}`, data);
  try {
    return await apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  } catch (error) {
    console.error(`❌ PUT ${endpoint} failed:`, error);
    throw error;
  }
},

delete: async (endpoint) => {
  console.log(`🔵 DELETE ${endpoint}`);
  try {
    return await apiCall(endpoint, { method: 'DELETE' });
  } catch (error) {
    console.error(`❌ DELETE ${endpoint} failed:`, error);
    throw error;
  }
},

// ==================== 🆕 REQUEST TIMEOUT UTILITY ====================

withTimeout: async (promise, timeoutMs = 30000) => {
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms`)), timeoutMs)
      )
    ]);
  } catch (error) {
    console.error('❌ Request timeout or failed:', error);
    throw error;
  }
},

// ==================== 🆕 BATCH OPERATIONS ====================

batchGetPollData: async (phaseNumbers = [1, 2, 3]) => {
  try {
    console.log(`📊 Batch fetching poll data for phases: ${phaseNumbers.join(', ')}`);

    const requests = phaseNumbers
      .filter(phase => phase >= 1 && phase <= 8)
      .map(phase => api.getPollDataByPhase(phase, 1, 10));

    const results = await Promise.all(requests);

    const batchData = phaseNumbers.reduce((acc, phase, idx) => {
      acc[`phase_${phase}`] = results[idx];
      return acc;
    }, {});

    console.log('✅ Batch poll data fetched:', batchData);
    return batchData;
  } catch (error) {
    console.error('❌ Error batch fetching poll data:', error);
    throw error;
  }
},

batchGetDistrictStats: async (phaseNumbers = [1, 2, 3]) => {
  try {
    console.log(`📊 Batch fetching district stats for phases: ${phaseNumbers.join(', ')}`);

    const requests = phaseNumbers
      .filter(phase => phase >= 1 && phase <= 8)
      .map(phase => api.getDistrictPollingStats(phase));

    const results = await Promise.all(requests);

    const batchStats = phaseNumbers.reduce((acc, phase, idx) => {
      acc[`phase_${phase}`] = results[idx];
      return acc;
    }, {});

    console.log('✅ Batch district stats fetched:', batchStats);
    return batchStats;
  } catch (error) {
    console.error('❌ Error batch fetching district stats:', error);
    throw error;
  }
},


  
  // ==================== ✅ NEW: AFFIDAVIT REQUEST ====================
  submitAffidavitRequest: async (affidavitData) => {
    try {
      console.log('📝 Submitting affidavit request');
      console.log('📦 Affidavit Data:', affidavitData);

      const response = await fetch(`${API_URL}/Affidavit/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(affidavitData)
      });

      console.log('📥 Affidavit response status:', response.status);

      const responseText = await response.text();
      console.log('📋 Response:', responseText);

      if (!response.ok) {
        try {
          const errorJson = JSON.parse(responseText);
          throw new Error(
            errorJson.message || errorJson.error || 'Affidavit submission failed'
          );
        } catch (parseErr) {
          throw new Error(responseText || 'Affidavit submission failed');
        }
      }

      const result = JSON.parse(responseText);
      console.log('✅ Affidavit submitted successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Affidavit submission error:', error);
      throw error;
    }
  },


  // ==================== SURVEY ANALYTICS ====================
  getSurveyStats: () => {
    console.log('📊 Fetching survey statistics');
    return apiCall('/SurveyAnalytics/stats');
  },

  getSurveyMainConcerns: () => {
    console.log('📋 Fetching main concerns analysis');
    return apiCall('/SurveyAnalytics/concerns');
  },

  getSurveyTopPriorities: () => {
    console.log('🎯 Fetching top priorities');
    return apiCall('/SurveyAnalytics/priorities');
  },

  getSurveyGovernmentPerformance: () => {
    console.log('⭐ Fetching government performance ratings');
    return apiCall('/SurveyAnalytics/performance');
  },

  getSurveyMostImportantIssues: () => {
    console.log('🔥 Fetching most important issues');
    return apiCall('/SurveyAnalytics/issues');
  },

  getSurveyCandidatePreferences: () => {
    console.log('🗳️ Fetching candidate preferences');
    return apiCall('/SurveyAnalytics/candidates');
  },

  getSurveyCMPreferences: () => {
    console.log('👑 Fetching CM preferences');
    return apiCall('/SurveyAnalytics/cm-preferences');
  },

  getSurveyDemographics: () => {
    console.log('👥 Fetching demographics');
    return apiCall('/SurveyAnalytics/demographics');
  },

  getSurveyWinnerPredictions: () => {
    console.log('🏆 Fetching winner predictions');
    return apiCall('/SurveyAnalytics/predictions');
  },

  getSurveyReport: () => {
    console.log('📈 Generating comprehensive survey report');
    return apiCall('/SurveyAnalytics/report');
  },

  getAllSurveys: (page = 1, pageSize = 20) => {
    console.log(`📋 Fetching surveys - Page: ${page}, Size: ${pageSize}`);
    return apiCall(`/VoterSurvey/all?page=${page}&pageSize=${pageSize}`);
  },

  searchSurveys: (query) => {
    console.log('🔍 Searching surveys:', query);
    return apiCall(`/VoterSurvey/search?query=${encodeURIComponent(query)}`);
  },

  getSurveyResultsByDistrict: (district) => {
    console.log('📊 Fetching survey results for district:', district);
    return apiCall(`/VoterSurvey/results/by-district/${encodeURIComponent(district)}`);
  },

  getSurveyResultsByConstituency: (constituency) => {
    console.log('📊 Fetching survey results for constituency:', constituency);
    return apiCall(`/VoterSurvey/results/by-constituency/${encodeURIComponent(constituency)}`);
  },

  getSurveyResultsByCandidates: () => {
    console.log('📊 Fetching survey results by candidates');
    return apiCall('/VoterSurvey/results/candidates');
  },

  getCMPreferenceAnalysis: () => {
    console.log('📊 Fetching CM preference analysis');
    return apiCall('/VoterSurvey/analytics/cm-preference');
  },

  getWinnerPredictions: () => {
    console.log('📊 Fetching winner predictions');
    return apiCall('/VoterSurvey/analytics/winner-predictions');
  },

  getMainConcerns: () => {
    console.log('📊 Fetching main concerns analysis');
    return apiCall('/VoterSurvey/analytics/concerns');
  },

  getGovernmentPerformance: () => {
    console.log('📊 Fetching government performance ratings');
    return apiCall('/VoterSurvey/analytics/govt-performance');
  },

  getTopPriorities: () => {
    console.log('📊 Fetching top priorities');
    return apiCall('/VoterSurvey/analytics/top-priorities');
  },

  getComprehensiveAnalytics: () => {
    console.log('📊 Fetching comprehensive survey analytics');
    return apiCall('/VoterSurvey/analytics/comprehensive');
  },

  getSurveysByDistrict: (district, page = 1, pageSize = 20) => {
    console.log(`📋 Fetching surveys for district: ${district} - Page: ${page}`);
    return apiCall(
      `/VoterSurvey/by-district/${encodeURIComponent(district)}?page=${page}&pageSize=${pageSize}`
    );
  },

  getSurveysByConstituency: (constituency, page = 1, pageSize = 20) => {
    console.log(`📋 Fetching surveys for constituency: ${constituency} - Page: ${page}`);
    return apiCall(
      `/VoterSurvey/by-constituency/${encodeURIComponent(constituency)}?page=${page}&pageSize=${pageSize}`
    );
  },

  // ==================== ADMIN - CONSTITUENCIES CRUD ====================
  addConstituency: (data) =>
    apiCall('/Admin/add-constituency', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  updateConstituency: async (id, data) => {
    try {
      console.log('📤 Updating constituency:', id, data);
      return await apiCall(`/Admin/update-constituency/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('❌ Update constituency error:', error);
      throw error;
    }
  },

  deleteConstituency: async (id) => {
    try {
      console.log('🗑️ Deleting constituency:', id);
      return await apiCall(`/Admin/delete-constituency/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('❌ Delete constituency error:', error);
      throw error;
    }
  },

  // ==================== ADMIN - CANDIDATES CRUD ====================
  addCandidate: async (data) => {
    try {
      console.log('📤 Adding candidate:', data);

      const cleanData = {
        constituencyCode: data.constituencyCode?.trim() || '',
        candidateName: data.candidateName?.trim() || '',
        partyShortName: data.partyShortName?.trim() || '',
        partyName: data.partyName?.trim() || '',
        age: data.age ? parseInt(data.age) : undefined,
        education: data.education?.trim() || undefined,
        profession: data.profession?.trim() || undefined,
        address: data.address?.trim() || undefined,
        photoUrl: data.photoUrl?.trim() || undefined,
        backgroundSourceUrl: data.backgroundSourceUrl?.trim() || undefined,
        affidavitPdfUrl: convertToAbsoluteUrl(data.affidavitPdfUrl?.trim()),
        assets: data.assets ? parseFloat(data.assets) : undefined,
        liabilities: data.liabilities ? parseFloat(data.liabilities) : undefined,
        criminalCases: data.criminalCases ? parseInt(data.criminalCases) : 0,
        email: data.email?.trim() || undefined,
        mobile: data.mobile
          ? String(data.mobile)
              .replace(/\D/g, '')
              .slice(0, 15)
          : undefined,
        customSymbol: data.customSymbol?.trim() || undefined
      };

      if (!cleanData.constituencyCode) throw new Error('❌ Constituency is required');
      if (!cleanData.candidateName) throw new Error('❌ Candidate name is required');
      if (!cleanData.partyShortName) throw new Error('❌ Party short name is required');
      if (!cleanData.partyName) throw new Error('❌ Party name is required');

      if (
        cleanData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanData.email)
      ) {
        throw new Error('❌ Invalid email address format');
      }
      if (cleanData.mobile && !/^\d{10,15}$/.test(cleanData.mobile)) {
        throw new Error('❌ Invalid mobile number (must be 10-15 digits)');
      }

      if (cleanData.affidavitPdfUrl && !isValidUrl(cleanData.affidavitPdfUrl)) {
        console.error('❌ Invalid affidavit URL:', cleanData.affidavitPdfUrl);
        throw new Error(`❌ Invalid affidavit PDF URL: ${cleanData.affidavitPdfUrl}`);
      }

      console.log('📊 Cleaned data:', JSON.stringify(cleanData, null, 2));

      return await apiCall('/Admin/add-candidate', {
        method: 'POST',
        body: JSON.stringify(cleanData)
      });
    } catch (error) {
      console.error('❌ Add candidate error:', error);
      throw error;
    }
  },

  updateCandidate: async (id, data) => {
    try {
      console.log('📤 Updating candidate:', id, data);

      const cleanData = {
        constituencyCode: data.constituencyCode?.trim() || '',
        candidateName: data.candidateName?.trim() || '',
        partyShortName: data.partyShortName?.trim() || '',
        partyName: data.partyName?.trim() || '',
        age: data.age ? parseInt(data.age) : undefined,
        education: data.education?.trim() || undefined,
        profession: data.profession?.trim() || undefined,
        address: data.address?.trim() || undefined,
        photoUrl: data.photoUrl?.trim() || undefined,
        backgroundSourceUrl: data.backgroundSourceUrl?.trim() || undefined,
        affidavitPdfUrl: convertToAbsoluteUrl(data.affidavitPdfUrl?.trim()),
        assets: data.assets ? parseFloat(data.assets) : undefined,
        liabilities: data.liabilities ? parseFloat(data.liabilities) : undefined,
        criminalCases: data.criminalCases ? parseInt(data.criminalCases) : 0,
        email: data.email?.trim() || undefined,
        mobile: data.mobile
          ? String(data.mobile)
              .replace(/\D/g, '')
              .slice(0, 15)
          : undefined,
        customSymbol: data.customSymbol?.trim() || undefined
      };

      if (!cleanData.constituencyCode) {
        throw new Error('❌ Constituency is required');
      }
      if (!cleanData.candidateName) {
        throw new Error('❌ Candidate name is required');
      }
      if (!cleanData.partyShortName) {
        throw new Error('❌ Party short name is required');
      }
      if (!cleanData.partyName) {
        throw new Error('❌ Party name is required');
      }

      if (
        cleanData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanData.email)
      ) {
        throw new Error('❌ Invalid email address format');
      }
      if (cleanData.mobile && !/^\d{10,15}$/.test(cleanData.mobile)) {
        throw new Error('❌ Invalid mobile number (must be 10-15 digits)');
      }

      if (cleanData.affidavitPdfUrl && !isValidUrl(cleanData.affidavitPdfUrl)) {
        console.error('❌ Invalid affidavit URL:', cleanData.affidavitPdfUrl);
        throw new Error(`❌ Invalid affidavit PDF URL: ${cleanData.affidavitPdfUrl}`);
      }

      console.log('📊 Cleaned data before sending:', JSON.stringify(cleanData, null, 2));

      const response = await fetch(`${API_URL}/Admin/update-candidate/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(cleanData)
      });

      console.log('📥 Response Status:', response.status, response.statusText);

      if (response.status === 401) {
        console.error('❌ Unauthorized (401)');
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized - Please login again');
      }

      if (response.status === 400) {
        const responseText = await response.text();
        console.error('❌ Bad Request (400) - Response Text:', responseText);

        try {
          const errorJson = JSON.parse(responseText);
          console.error('❌ Error Details:', errorJson);

          if (errorJson.errors) {
            console.error('❌ Validation Errors Object:');
            Object.entries(errorJson.errors).forEach(([field, messages]) => {
              const message = Array.isArray(messages) ? messages.join(', ') : messages;
              console.error(`  ❌ ${field}: ${message}`);
            });

            const errorMessages = Object.entries(errorJson.errors)
              .map(([field, messages]) => {
                const msg = Array.isArray(messages) ? messages.join(', ') : messages;
                return `${field}: ${msg}`;
              })
              .join('\n');

            throw new Error(`Validation Error:\n${errorMessages}`);
          }

          throw new Error(
            errorJson.message || 
            errorJson.title || 
            'Validation failed - Please check your input'
          );
        } catch (parseErr) {
          console.error('❌ Error parsing response:', parseErr);
          throw new Error(`Validation failed: ${responseText}`);
        }
      }

      if (!response.ok) {
        const responseText = await response.text();
        console.error(`❌ HTTP Error ${response.status}:`, responseText);
        throw new Error(`Update failed with status ${response.status}: ${responseText}`);
      }

      const result = await response.json();
      console.log('✅ Candidate updated successfully:', result);
      return result;

    } catch (error) {
      console.error('❌ Update candidate error:', error.message);
      console.error('❌ Full Error Stack:', error);
      throw error;
    }
  },

  deleteCandidate: async (id) => {
    try {
      console.log('🗑️ Deleting candidate:', id);
      return await apiCall(`/Admin/delete-candidate/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('❌ Delete candidate error:', error);
      throw error;
    }
  },

  getCandidates: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.constituencyCode)
        queryParams.append('constituencyCode', params.constituencyCode);
      if (params.partyShortName)
        queryParams.append('partyShortName', params.partyShortName);
      if (params.page) queryParams.append('page', params.page);
      if (params.pageSize) queryParams.append('pageSize', params.pageSize);

      const queryString = queryParams.toString();
      const endpoint = `/Admin/candidates${queryString ? `?${queryString}` : ''}`;

      return await apiCall(endpoint);
    } catch (error) {
      console.error('❌ Get candidates error:', error);
      throw error;
    }
  },

  // ==================== ADMIN - SCRAPER ====================
  scrapeECIAffidavits: () =>
    apiCall('/Admin/scrape-eci-affidavits', {
      method: 'POST'
    }),

  getScraperStatus: () => apiCall('/Admin/scraper-status'),

  // ==================== SLIDER MANAGEMENT ====================
  getSliders: () => apiCall('/Slider'),
  getActiveSliders: () => apiCall('/Slider/active'),
  getSliderById: (id) => apiCall(`/Slider/${id}`),

  createSlider: async (formData) => {
    const token = authService.getToken();

    try {
      console.log('📤 Creating slider');

      const response = await fetch(`${API_URL}/Slider`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.message || errorJson.error || 'Failed to create slider';
        } catch {
          errorMessage = errorText || 'Failed to create slider';
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Create slider error:', error);
      throw error;
    }
  },

  updateSlider: async (id, formData) => {
    const token = authService.getToken();

    try {
      console.log('📤 Updating slider:', id);

      const response = await fetch(`${API_URL}/Slider/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.message || errorJson.error || 'Failed to update slider';
        } catch {
          errorMessage = errorText || 'Failed to update slider';
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Update slider error:', error);
      throw error;
    }
  },

  deleteSlider: (id) =>
    apiCall(`/Slider/${id}`, {
      method: 'DELETE'
    }),

  toggleSliderStatus: (id) =>
    apiCall(`/Slider/${id}/toggle-status`, {
      method: 'POST'
    }),

  // ==================== CANDIDATE PHOTO UPLOAD ====================
  uploadCandidatePhoto: async (file) => {
    const token = authService.getToken();

    try {
      console.log('📸 Uploading candidate photo:', file.name);

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image size must be less than 5MB');
      }

      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`${API_URL}/Admin/upload-candidate-photo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.message || errorJson.error || 'Failed to upload photo';
        } catch {
          errorMessage = errorText || 'Failed to upload photo';
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Photo uploaded successfully:', result);
      return result;
    } catch (error) {
      console.error('Photo upload error:', error);
      throw error;
    }
  },

  // ==================== AFFIDAVIT PDF UPLOAD ====================
  uploadAffidavitPdf: async (file) => {
    const token = authService.getToken();

    try {
      console.log('📄 Uploading affidavit PDF:', file.name);

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('PDF size must be less than 10MB');
      }

      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      const formData = new FormData();
      formData.append('pdf', file);

      console.log('📤 POST', `${API_URL}/Admin/upload-affidavit-pdf`);
      console.log('📦 File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const response = await fetch(`${API_URL}/Admin/upload-affidavit-pdf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      console.log('📥 Response Status:', response.status);

      if (response.status === 401) {
        console.error('❌ Unauthorized (401)');
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized - Please login again');
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.message || errorJson.error || 'Failed to upload affidavit PDF';
        } catch {
          errorMessage = errorText || 'Failed to upload affidavit PDF';
        }

        console.error('❌ Upload error:', errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Affidavit PDF uploaded successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Affidavit PDF upload error:', error);
      throw error;
    }
  },

  // ==================== FILE UPLOADS ====================
  uploadFile: async (endpoint, file) => {
    const token = authService.getToken();

    try {
      console.log('📤 Uploading file:', {
        endpoint,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      if (!file) {
        throw new Error('No file provided');
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      console.log('📥 Upload response status:', response.status);

      if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Unauthorized - Please login again');
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Upload failed with status ${response.status}`;

        try {
          if (contentType && contentType.includes('application/json')) {
            const errorJson = await response.json();
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } else {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          }
        } catch (parseErr) {
          console.error('Error parsing response:', parseErr);
        }

        console.error('❌ Upload error response:', {
          status: response.status,
          message: errorMessage,
          endpoint
        });

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Upload error:', {
        message: error.message,
        endpoint,
        fileName: file?.name,
        fileSize: file?.size
      });
      throw error;
    }
  },

  // ==================== VOTE COUNTING (ADMIN) ====================

getVotingConstituencies: () => {
  console.log('📍 Fetching voting constituencies');
  return apiCall('/ElectionResults/constituencies');
},

getCandidatesForVoting: (constituencyId) => {
  console.log('👥 Fetching candidates for voting:', constituencyId);
  return apiCall(`/ElectionResults/constituencies/${constituencyId}/candidates`);
},

getConstituencyResults: (constituencyId) => {
  console.log('📊 Fetching constituency results:', constituencyId);
  return apiCall(`/ElectionResults/constituencies/${constituencyId}/results`);
},

updateVotes: (voteData) => {
  console.log('✅ Updating votes:', voteData);
  return apiCall('/ElectionResults/update-votes', {
    method: 'POST',
    body: JSON.stringify(voteData)
  });
},

bulkUpdateVotes: (bulkData) => {
  console.log('📦 Bulk updating votes:', bulkData);
  return apiCall('/ElectionResults/bulk-update', {
    method: 'POST',
    body: JSON.stringify(bulkData)
  });
},

getResultsSummary: () => {
  console.log('📈 Fetching results summary');
  return apiCall('/ElectionResults/summary');
},

getPartySummary: () => {
  console.log('🎉 Fetching party-wise summary');
  return apiCall('/ElectionResults/party-summary');
},

getAllCandidatesWithResults: async () => {
  console.log('🎉 Fetching party-wise summary');
  return apiCall('/ElectionResults/all-candidates');
},



  // ==================== HEALTH CHECK ====================
  healthCheck: () => apiCall('/Health').catch(() => null)
};

export default api;
